import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Eye, CheckCircle, Clock, TrendingUp, DollarSign, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { SkeletonTable } from '../components/ui/skeleton';
import { ErrorCard } from '../components/error/ErrorCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useDistributions } from '../hooks/useQueries';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/formatters';
import type { Distribution } from '../services/distributions.service';

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'draft':
      return <Clock className="h-4 w-4 text-gray-500" />;
    case 'approved':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'distributed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    default:
      return null;
  }
};

const getStatusColor = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case 'draft':
      return "outline";
    case 'approved':
      return "default";
    case 'distributed':
      return "secondary";
    default:
      return "outline";
  }
};

export function DistributionsPage() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  
  const { data: distributions, error, isLoading } = useDistributions();

  const filteredDistributions = distributions?.filter((distribution: Distribution) => {
    const searchLower = searchTerm.toLowerCase();
    return distribution.year?.toString().includes(searchLower);
  }) || [];

  const handleCreateDistribution = () => {
    // TODO: Navigate to create distribution page or open modal
  };

  const handleViewDetails = (distributionId: string) => {
    // TODO: Navigate to distribution details page
    console.log('View distribution:', distributionId);
  };

  const handleApproveDistribution = (distributionId: string) => {
    // TODO: Call approve distribution API
    console.log('Approve distribution:', distributionId);
  };

  const handleExecuteDistribution = (distributionId: string) => {
    // TODO: Call execute distribution API
    console.log('Execute distribution:', distributionId);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('distributions.title')}</h1>
          <p className="mt-2 text-gray-600">{t('distributions.subtitle')}</p>
        </div>
        <SkeletonTable rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('distributions.title')}</h1>
          <p className="mt-2 text-gray-600">{t('distributions.subtitle')}</p>
        </div>
        <ErrorCard 
          error={error} 
          onRetry={() => window.location.reload()} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('distributions.title')}</h1>
          <p className="mt-2 text-gray-600">{t('distributions.subtitle')}</p>
        </div>
        <Button onClick={handleCreateDistribution}>
          <Plus className="h-4 w-4 mr-2" />
          {t('distributions.createDistribution')}
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('common.search')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            type="search"
            placeholder={t('distributions.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </CardContent>
      </Card>

      {/* Distributions Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('distributions.table.distributionId')}</TableHead>
                  <TableHead>{t('distributions.table.year')}</TableHead>
                  <TableHead>{t('distributions.table.totalAmount')}</TableHead>
                  <TableHead>{t('distributions.table.profitAmount')}</TableHead>
                  <TableHead>{t('distributions.table.memberCount')}</TableHead>
                  <TableHead>{t('distributions.table.perMember')}</TableHead>
                  <TableHead>{t('distributions.table.createdAt')}</TableHead>
                  <TableHead>{t('distributions.table.status')}</TableHead>
                  <TableHead className="text-right">{t('distributions.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDistributions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-gray-500 py-8">
                      {t('common.noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDistributions.map((distribution) => (
                    <TableRow key={distribution.distributionId}>
                      <TableCell className="font-mono text-sm">
                        {distribution.distributionId?.slice(0, 8)}...
                      </TableCell>
                      <TableCell className="font-semibold">{distribution.year}</TableCell>
                      <TableCell>{formatCurrency(distribution.totalAmount || 0)}</TableCell>
                      <TableCell>{formatCurrency(distribution.profitAmount || 0)}</TableCell>
                      <TableCell>{distribution.memberCount}</TableCell>
                      <TableCell>
                        {formatCurrency(
                          distribution.memberCount && distribution.totalAmount 
                            ? distribution.totalAmount / distribution.memberCount 
                            : 0
                        )}
                      </TableCell>
                      <TableCell>
                        {distribution.createdAt ? format(new Date(distribution.createdAt), 'dd/MM/yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(distribution.status || '')}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(distribution.status || '')}
                            {t(`distributions.status.${distribution.status}`)}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(distribution.distributionId)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {distribution.status === 'draft' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => handleApproveDistribution(distribution.distributionId)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {distribution.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleExecuteDistribution(distribution.distributionId)}
                            >
                              {t('distributions.execute')}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('distributions.summary.totalDistributions')}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredDistributions.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('distributions.summary.totalDistributed')}</CardTitle>
            <DollarSign className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(
                filteredDistributions
                  .filter(d => d.status === 'distributed')
                  .reduce((sum, dist) => sum + (dist.totalAmount || 0), 0)
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('distributions.summary.totalProfit')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(
                filteredDistributions
                  .filter(d => d.status === 'distributed')
                  .reduce((sum, dist) => sum + (dist.profitAmount || 0), 0)
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('distributions.summary.pendingApproval')}</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredDistributions.filter(d => d.status === 'draft').length}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}